import { useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import {
  DataView,
  Disclosure,
  FormatMetadata,
  humanize,
  LongText,
  record,
  timestamp,
} from './DataView';
import type { AdminTool } from './types';
import {
  AssistantEmailAttachment,
  EmailDraftAttachments,
} from '@pv/common/assistants';

const text = (value: unknown) =>
  typeof value === 'string' ? value : undefined;
const remainder = (value: Record<string, unknown>, keys: string[]) =>
  Object.fromEntries(
    Object.entries(value).filter(([key]) => !keys.includes(key))
  );

const activityTitle = (kind: unknown) => {
  switch (kind) {
    case 'phone':
      return 'Logged phone call';
    case 'meeting':
      return 'Logged meeting';
    case 'sms':
      return 'Logged text message';
    case 'email':
      return 'Logged external email';
    default:
      return 'Logged offline communication';
  }
};

function AdditionalFields({
  value,
  label = 'Additional fields',
  metadata,
}: {
  value: Record<string, unknown>;
  label?: string;
  metadata?: FormatMetadata;
}) {
  return Object.keys(value).length ? (
    <Disclosure label={label}>
      <DataView value={value} metadata={metadata} />
    </Disclosure>
  ) : null;
}

function RecordList({
  rows,
  communications = false,
  contacts = false,
  metadata,
}: {
  rows: Record<string, unknown>[];
  communications?: boolean;
  contacts?: boolean;
  metadata: FormatMetadata;
}) {
  const [limit, setLimit] = useState(10);
  if (!rows.length)
    return (
      <Typography variant="body2" color="text.secondary">
        No records returned.
      </Typography>
    );
  return (
    <Box>
      {rows.slice(0, limit).map((row, index) => (
        <Paper variant="outlined" key={index} sx={{ p: 1.5, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ overflowWrap: 'anywhere' }}>
            {(communications &&
              row.entry_type === 'offline_communication' &&
              activityTitle(row.kind)) ||
              (contacts &&
                [text(row.first_name), text(row.last_name)]
                  .filter(Boolean)
                  .join(' ')) ||
              text(row.subject) ||
              text(row.name) ||
              `Record ${index + 1}`}
          </Typography>
          {contacts && typeof row.email === 'string' && (
            <Typography variant="body2" color="text.secondary">
              {row.email}
            </Typography>
          )}
          {communications && row.entry_type === 'offline_communication' ? (
            <>
              <Typography variant="body2" color="text.secondary">
                {text(record(row.user)?.full_name) || 'User unavailable'}
                {' · Contact: '}
                {text(record(row.planner)?.full_name) || 'Contact unavailable'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {timestamp(
                  text(row.timeline_at) ?? text(row.occurred_at),
                  metadata.timezone
                )}
              </Typography>
              <Disclosure label="Activity note">
                <LongText text={text(row.note) ?? 'No note recorded.'} />
                {row.note_truncated === true && (
                  <Typography variant="caption">Note truncated.</Typography>
                )}
              </Disclosure>
              <AdditionalFields
                value={remainder(row, [
                  'kind',
                  'user',
                  'planner',
                  'occurred_at',
                  'timeline_at',
                  'note',
                  'note_truncated',
                ])}
                metadata={metadata}
              />
            </>
          ) : communications ? (
            <>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{ mt: 0.5, mb: 1 }}
              >
                <Typography variant="caption" color="text.secondary">
                  {text(row.sender) || 'Sender unavailable'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.timeline_at ? 'Timeline: ' : ''}
                  {timestamp(
                    text(row.timeline_at) ?? text(row.sent_at),
                    metadata.timezone
                  )}
                </Typography>
              </Stack>
              <Disclosure label="Email body">
                <LongText text={text(row.body) ?? 'No body recorded.'} />
                {row.body_truncated === true && (
                  <Typography variant="caption">Body truncated.</Typography>
                )}
              </Disclosure>
              <AdditionalFields
                value={remainder(row, [
                  'subject',
                  'sender',
                  'body',
                  'body_truncated',
                  ...(row.timeline_at ? [] : ['sent_at']),
                ])}
                metadata={metadata}
              />
            </>
          ) : (
            <Disclosure label={contacts ? 'Contact details' : 'Record details'}>
              <DataView value={row} metadata={metadata} />
            </Disclosure>
          )}
        </Paper>
      ))}
      {rows.length > limit && (
        <Button size="small" onClick={() => setLimit(limit + 10)}>
          Show more records ({rows.length - limit} remaining)
        </Button>
      )}
    </Box>
  );
}

function Draft({
  value,
  additions,
}: {
  value: Record<string, unknown>;
  additions: boolean;
}) {
  return (
    <Box sx={{ maxWidth: '80ch' }}>
      <Typography variant="overline" color="text.secondary">
        Subject
      </Typography>
      <LongText text={value.subject as string} />
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: 'block', mt: 2 }}
      >
        Message
      </Typography>
      <LongText text={value.body as string} />
      <EmailDraftAttachments
        additions={additions}
        attachments={
          Array.isArray(value.attachments)
            ? (value.attachments as AssistantEmailAttachment[])
            : undefined
        }
      />
      <AdditionalFields
        value={remainder(value, ['subject', 'body', 'attachments'])}
      />
    </Box>
  );
}

function EventFields({
  data,
  metadata,
}: {
  data: unknown;
  metadata: FormatMetadata;
}) {
  const fields = record(data)!;
  const contacts = fields.contacts;
  if (!Array.isArray(contacts) || !contacts.every((item) => !!record(item)))
    return <DataView value={data} metadata={metadata} />;
  return (
    <>
      <DataView value={remainder(fields, ['contacts'])} metadata={metadata} />
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Contacts
      </Typography>
      <RecordList rows={contacts} contacts metadata={metadata} />
    </>
  );
}

export function resultSummary(tool: AdminTool): string {
  const output = record(tool.output);
  const args = record(tool.arguments);
  if (tool.output == null) return 'No result recorded.';
  if (output && !Object.keys(output).length)
    return ['pending', 'running', 'waiting'].includes(tool.status)
      ? 'Awaiting result.'
      : 'Empty result.';
  if (['present_email_draft', 'read_email_draft'].includes(tool.name)) {
    const draft = record(output?.draft) || output;
    if (typeof draft?.subject === 'string')
      return `Draft: ${draft.subject.slice(0, 160) || '(no subject)'}`;
  }
  if (tool.name === 'read_email_thread' && Array.isArray(output?.data)) {
    const activities = output!.data.filter(
      (item) => record(item)?.entry_type === 'offline_communication'
    ).length;
    const emails = output!.data.length - activities;
    const counts = [];
    if (emails || !activities)
      counts.push(`${emails} ${emails === 1 ? 'email' : 'emails'}`);
    if (activities)
      counts.push(
        `${activities} logged ${activities === 1 ? 'activity' : 'activities'}`
      );
    return `${counts.join(' and ')} returned${
      output!.next_offset != null ? ' · more available at source' : ''
    }`;
  }
  if (Array.isArray(output?.data))
    return `${output!.data.length} ${
      output!.data.length === 1 ? 'record' : 'records'
    } returned${
      output!.next_offset != null ? ' · more available at source' : ''
    }`;
  if (tool.name === 'read_event' && typeof args?.section === 'string')
    return `Event ${args.section} returned`;
  return 'Result recorded';
}

export default function ToolResult({ tool }: { tool: AdminTool }) {
  const output = record(tool.output);
  const metadata: FormatMetadata = {
    currency: text(output?.currency),
    moneyUnit: text(output?.money_unit),
    timezone: text(output?.timezone),
    timeOffsetUnit: text(output?.time_offset_unit),
  };
  if (tool.output === undefined || tool.output === null)
    return (
      <Typography variant="body2" color="text.secondary">
        No result recorded.
      </Typography>
    );
  if (
    output &&
    ['present_email_draft', 'read_email_draft'].includes(tool.name)
  ) {
    const nested = record(output.draft);
    const draft = nested || output;
    if (typeof draft.subject === 'string' && typeof draft.body === 'string')
      return (
        <>
          <Draft
            value={draft}
            additions={tool.name === 'present_email_draft'}
          />
          {nested && (
            <AdditionalFields
              label="Source and metadata"
              value={remainder(output, ['draft'])}
            />
          )}
        </>
      );
  }
  if (output && ['read_event', 'read_email_thread'].includes(tool.name)) {
    const data = output.data;
    const section = record(tool.arguments)?.section;
    if (
      record(data) ||
      (Array.isArray(data) && data.every((item) => !!record(item)))
    )
      return (
        <>
          {typeof section === 'string' && (
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {humanize(section)}
            </Typography>
          )}
          {Array.isArray(data) ? (
            <RecordList
              rows={data as Record<string, unknown>[]}
              communications={
                tool.name === 'read_email_thread' &&
                data.every((item) =>
                  (item.entry_type === 'offline_communication'
                    ? ['note']
                    : ['subject', 'sender', 'sent_at', 'body']
                  ).every(
                    (key) => typeof item[key] === 'string' || item[key] == null
                  )
                )
              }
              metadata={metadata}
            />
          ) : (
            <EventFields data={data} metadata={metadata} />
          )}
          <AdditionalFields
            label="Source and metadata"
            value={remainder(output, ['data'])}
            metadata={metadata}
          />
        </>
      );
  }
  return <DataView value={tool.output} metadata={metadata} />;
}
